import { computed, getCurrentScope, onScopeDispose, readonly, ref, toValue, type MaybeRefOrGetter } from 'vue';
import {
  getGenerationForm,
  type GeneratedRecordIdentificationWarning,
} from '@/entities/GeneratedRecord';
import { extractApiErrorMessage, extractApiFieldErrors } from '@/shared/lib';

export const INSPECTION_IDENTIFIER_REQUIRED_MESSAGE = 'Campo obrigatório.';
export const INSPECTION_IDENTIFIER_QUERY_FAILED_MESSAGE =
  'Não foi possível consultar o Protheus. Tente novamente.';

export function useInspectionIdentifier(
  matrixId: MaybeRefOrGetter<number>,
  onIdentified: (identifier: string) => void
) {
  const identifier = ref('');
  const missing = ref(false);
  const error = ref<string | null>(null);
  const warnings = ref<GeneratedRecordIdentificationWarning[]>([]);
  const querying = ref(false);
  // The number the shown warnings belong to: "Continuar" goes on with it, never with a retyped one.
  let warnedIdentifier: string | null = null;
  let attempt = 0;

  const requiredError = computed(() => (missing.value ? INSPECTION_IDENTIFIER_REQUIRED_MESSAGE : ''));
  const submitLabel = computed(() => (warnings.value.length > 0 ? 'Continuar' : 'Criar'));

  function clearFeedback(): void {
    missing.value = false;
    error.value = null;
    warnings.value = [];
    warnedIdentifier = null;
  }

  function setIdentifier(value: string): void {
    identifier.value = value;
    clearFeedback();
  }

  async function submit(): Promise<void> {
    if (querying.value) return;

    if (warnedIdentifier !== null) {
      onIdentified(warnedIdentifier);
      return;
    }

    const typed = identifier.value.trim();
    if (typed === '') {
      missing.value = true;
      return;
    }

    clearFeedback();
    querying.value = true;
    const current = ++attempt;

    try {
      const form = await getGenerationForm(toValue(matrixId), { identifier: typed });
      if (current !== attempt) return;

      const accepted = form.identification?.identifier ?? typed;
      const found = form.identification?.warnings ?? [];
      if (found.length === 0) {
        onIdentified(accepted);
        return;
      }

      warnings.value = found;
      warnedIdentifier = accepted;
    } catch (caught) {
      if (current !== attempt) return;
      // A refused field answers the generic "Erro de validação" in `message`; the reason is in `errors`.
      error.value =
        extractApiFieldErrors(caught).identifier?.[0] ??
        extractApiErrorMessage(caught) ??
        INSPECTION_IDENTIFIER_QUERY_FAILED_MESSAGE;
    } finally {
      if (current === attempt) querying.value = false;
    }
  }

  // A closed modal must not navigate when a query it started answers later.
  function discard(): void {
    attempt += 1;
    identifier.value = '';
    querying.value = false;
    clearFeedback();
  }

  if (getCurrentScope()) {
    onScopeDispose(discard);
  }

  return {
    identifier: readonly(identifier),
    requiredError,
    error: readonly(error),
    warnings: readonly(warnings),
    querying: readonly(querying),
    submitLabel,
    setIdentifier,
    submit,
    discard,
  };
}
